import { useQuery } from '@tanstack/react-query';
import { DEFAULT_NODALS_MAY_AUTHOR_ARRANGEMENTS, type ArrangementAuthoringSettingDto } from '@pams/shared';
import { can } from '@/lib/accessRole';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { useAuth } from '@/hooks/useAuth';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [UX1 §8] MAY A NODAL ADMIN RAISE AN ARRANGEMENT ON THIS DEPLOYMENT?
 *
 *  ⚠️ IT IS A LIVE READ, AND `GET /auth/config` WAS THE WRONG CARRIER FOR IT.
 *
 *  The UI mode rides on that endpoint, and `UiModeSync` fetches it with no query key and
 *  deliberately writes the answer to localStorage FOR THE NEXT LOAD — its own header says "IT
 *  MUST NOT APPLY WHAT IT LEARNS". For a palette that is right; for a capability it would mean
 *  a TIGHTENING takes effect one load late, which is the fail-open direction. It is also a
 *  PUBLIC endpoint, and a deployment's authorisation policy is not something to hand an
 *  anonymous caller.
 *
 *  ⚠️ THE FETCH IS GATED ON THE SAME CAPABILITY THE ROUTE TAKES. `GET /settings/arrangement-authoring`
 *  is `arrangement.write`, so a NODAL_READ or NODAL_MONITOR would get a 403 — and
 *  `useNotifications`' rule is that a hook must not fire a request its caller's role cannot
 *  make. They fall to the default, which hides the action they could not use anyway.
 *
 *  ⚠️ `enabled &&` ON `isPending` IS NOT DECORATION. TanStack v5 reports a DISABLED query as
 *  `isPending` for ever, so without it `RequireDivision` would spin permanently for a role that
 *  never fires the request — a blank screen instead of a decision.
 *
 *  ⚠️ AND A FAILURE ANSWERS THE DEFAULT, WHICH IS `false`. An unreadable capability must not be
 *  granted; the server refuses the write either way, so the worst case is a door that is hidden
 *  from somebody entitled to it rather than offered to somebody who is not.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const useArrangementAuthoringSetting = (): {
  nodalsMayAuthor: boolean;
  isPending: boolean;
  enabled: boolean;
} => {
  const { officer } = useAuth();
  const enabled = officer !== undefined && can(officer.accessRole, 'arrangement.write');

  const query = useQuery({
    queryKey: queryKeys.settings.arrangementAuthoring(),
    queryFn: ({ signal }) => api.get<ArrangementAuthoringSettingDto>('/settings/arrangement-authoring', { signal }),
    enabled,
    staleTime: 60_000,
  });

  return {
    nodalsMayAuthor: query.data?.nodalsMayAuthorArrangements ?? DEFAULT_NODALS_MAY_AUTHOR_ARRANGEMENTS,
    isPending: enabled && query.isPending,
    enabled,
  };
};
